import { useState } from 'react';
import type { CorrectionReviewRow, CorrectionStatus } from '@dru/types';
import {
  CORRECTION_OUTCOME_LABEL, CORRECTION_STATUS_LABEL, CORRECTION_TYPE_LABEL, EXAM_TYPE_LABEL, FITNESS_LABEL,
} from '@dru/types';
import {
  Alert, Card, ConfirmDialog, EmptyState, FilterBar, IconAction, RowActions, StatusBadge, TextSkeleton, fitnessTone,
} from '@dru/ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { formatDateTime } from '../../../lib/format';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { ReopenCaseButton, ReviewPolicyCard } from '../components/ReviewAssuranceAdmin';

/**
 * Physicians asking to correct their own signed reviews.
 *
 * Approving does not change the review — nothing does. It opens a re-review
 * on the same evidence, in the physician's own queue, which they sign under
 * their own registration; the review they signed first stays on the record.
 * Rejecting leaves everything as it is, with the reason kept.
 *
 * The decision here is whether a re-review is warranted, not what it should
 * conclude: that is the physician's clinical act, and this permission does not
 * carry it.
 */
export function ReviewCorrections() {
  const { permissions } = useAuth();
  const allowed = holds(permissions, 'review.correction-approve');
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const status = filters.status ?? 'PENDING';
  const list = useQuery<{ items: CorrectionReviewRow[] }>(
    () => (allowed ? api.get(`/platform/review-corrections?status=${status}`) : Promise.resolve({ items: [] })),
    [status, allowed],
  );
  const [acting, setActing] = useState<{ row: CorrectionReviewRow; act: 'approve' | 'reject' } | null>(null);

  if (!allowed) {
    return (
      <div className="space-y-4">
        <PageHeader className="mb-0" title="Review corrections" />
        <EmptyState title="Not available to your account" hint="Correction requests are decided by a Super Admin." />
      </div>
    );
  }

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Review corrections"
        hint="A physician’s request to correct a review they signed. Approving opens a re-review they sign again; the signed review is never edited."
        actions={holds(permissions, 'review.correction-request') ? <ReopenCaseButton onDone={list.reload} /> : undefined}
      />
      {holds(permissions, 'system.settings-manage') && <ReviewPolicyCard />}
      <FilterBar
        filters={[{
          key: 'status', label: 'Status', allLabel: 'Awaiting approval',
          options: [
            ...(['APPROVED', 'REJECTED', 'COMPLETED', 'CANCELLED'] as CorrectionStatus[])
              .map((s): { value: string; label: string } => ({ value: s, label: CORRECTION_STATUS_LABEL[s] })),
            { value: 'ALL', label: 'All requests' },
          ],
        }]}
        value={filters}
        onChange={setFilters}
      />

      {list.error && <Alert tone="danger">{list.error}</Alert>}
      {list.loading && !list.data ? (
        <Card className="p-4"><TextSkeleton lines={5} /></Card>
      ) : rows.length === 0 ? (
        <EmptyState
          title={status === 'PENDING' ? 'Nothing is waiting for a decision' : 'No requests in this state'}
          hint="A request appears here when a physician asks to correct one of their signed reviews."
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Card className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-body font-semibold text-neutral-900">{r.order.employee.fullName}</div>
                    <div className="text-caption font-normal text-neutral-500">
                      <span className="font-mono">{r.order.employee.employeeCode}</span>
                      {' · '}{r.company}{r.order.employee.plant ? ` · ${r.order.employee.plant.name}` : ''}
                      {' · '}{EXAM_TYPE_LABEL[r.order.examinationType] ?? r.order.examinationType}
                    </div>
                  </div>
                  <StatusBadge tone={r.status === 'PENDING' ? 'pending' : r.status === 'REJECTED' ? 'warning' : 'neutral'}>
                    {CORRECTION_STATUS_LABEL[r.status]}
                  </StatusBadge>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2 text-caption text-neutral-600">
                  <span>Signed by <span className="font-medium text-neutral-800">{r.physician.fullName}</span> ({r.physician.registrationNo})</span>
                  {r.review.signedAt && <span>· {formatDateTime(r.review.signedAt)}</span>}
                  {r.review.decision && <StatusBadge tone={fitnessTone(r.review.decision)} className="py-0">{FITNESS_LABEL[r.review.decision]}</StatusBadge>}
                  {r.review.certificate && (
                    <span className="font-mono">{r.review.certificate.certificateNumber} · {r.review.certificate.status.toLowerCase()}</span>
                  )}
                </div>

                <div className="mt-3 rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small">
                  <p className="text-caption text-neutral-500">
                    {CORRECTION_TYPE_LABEL[r.type]} · requested {formatDateTime(r.requestedAt)}
                  </p>
                  <p className="mt-0.5 font-medium text-neutral-900">{r.reason}</p>
                  <p className="mt-0.5 whitespace-pre-line text-neutral-700">{r.detail}</p>
                </div>

                {r.resolutionNote && <p className="mt-2 text-caption text-neutral-600">Decision note: “{r.resolutionNote}”</p>}
                {r.status === 'COMPLETED' && (
                  <p className="mt-1 text-caption text-neutral-600">
                    {r.decisionChanged ? 'Decision changed on re-review.' : 'Decision unchanged on re-review.'}
                    {r.certificateOutcome ? ` ${CORRECTION_OUTCOME_LABEL[r.certificateOutcome]}.` : ''}
                  </p>
                )}

                {r.status === 'PENDING' && (
                  <RowActions className="mt-3">
                    <IconAction
                      label={`Reject the correction for ${r.order.employee.fullName}`}
                      icon={XCircle} danger
                      onClick={() => setActing({ row: r, act: 'reject' })}
                    />
                    <IconAction
                      label={`Approve a re-review for ${r.order.employee.fullName}`}
                      icon={CheckCircle2} primary
                      onClick={() => setActing({ row: r, act: 'approve' })}
                    />
                  </RowActions>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      {acting && (
        <ConfirmDialog
          title={acting.act === 'approve' ? 'Approve a re-review' : 'Reject the correction'}
          what={acting.act === 'approve'
            ? `The examination returns to ${acting.row.physician.fullName}’s queue as a correction re-review on the same evidence. They sign it again; the review they signed first is kept exactly as it is, and its certificate changes only if the new signature says so.`
            : 'The signed review, its certificate and everything else stay exactly as they are. The physician sees your reason.'}
          affects={`${acting.row.order.employee.fullName} · ${acting.row.company} · ${CORRECTION_TYPE_LABEL[acting.row.type]}`}
          confirmLabel={acting.act === 'approve' ? 'Approve re-review' : 'Reject'}
          tone={acting.act === 'approve' ? 'primary' : 'danger'}
          requireReason={acting.act === 'reject'}
          minReason={10}
          reasonLabel={acting.act === 'reject' ? 'Why it is refused' : undefined}
          reasonHint={acting.act === 'reject' ? 'At least ten characters. The physician reads this.' : undefined}
          onConfirm={async (reason) => {
            const path = `/platform/tenants/${acting.row.tenantId}/review-corrections/${acting.row.id}/${acting.act}`;
            await api.post(path, acting.act === 'reject' ? { note: reason } : {}, acting.act === 'approve' ? crypto.randomUUID() : undefined);
            setActing(null);
            list.reload();
          }}
          onCancel={() => setActing(null)}
        />
      )}
    </div>
  );
}
